import React, { useState, useEffect } from 'react';
import api from '../../services/api';
import { useToast } from '../../contexts/ToastContext';
import DeleteConfirmModal from '../../components/admin/DeleteConfirmModal';
import SimpleFormModal from '../../components/SimpleFormModal';
import './AdminStretcherBars.css';

interface StretcherBar {
  id: number;
  length: number;
  stockQuantity: number;
  lowStockThreshold: number | null;
  trackInventory: boolean;
  isInStock: boolean;
  isLowStock: boolean;
  createdAt: string;
  updatedAt: string | null;
}

interface BarFormData {
  length: string;
  stockQuantity: string;
  lowStockThreshold: string;
  trackInventory: boolean;
}

const AdminStretcherBars: React.FC = () => {
  const [bars, setBars] = useState<StretcherBar[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [showForm, setShowForm] = useState<boolean>(false);
  const [editingBar, setEditingBar] = useState<StretcherBar | null>(null);
  const [saving, setSaving] = useState<boolean>(false);
  const [confirmDeleteId, setConfirmDeleteId] = useState<number | null>(null);
  const [deleting, setDeleting] = useState<boolean>(false);
  const [adjustTargetId, setAdjustTargetId] = useState<number | null>(null);
  const [adjustMode, setAdjustMode] = useState<'add' | 'remove' | null>(null);
  const [adjusting, setAdjusting] = useState<boolean>(false);
  const { success, error: showError, warning } = useToast();
  const [formData, setFormData] = useState<BarFormData>({
    length: '',
    stockQuantity: '',
    lowStockThreshold: '',
    trackInventory: true
  });

  useEffect(() => {
    fetchBars();
  }, []);

  const fetchBars = async (): Promise<void> => {
    try {
      const response = await api.get<StretcherBar[]>('/admin/stretcher-bars');
      setBars(response.data);
    } catch (error) {
      console.error('Failed to fetch bars:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    const length = parseFloat(formData.length);
    const stockQuantity = parseInt(formData.stockQuantity);
    if (Number.isNaN(length) || length <= 0) {
      warning('Length must be greater than 0.');
      return;
    }
    if (Number.isNaN(stockQuantity) || stockQuantity < 0) {
      warning('Stock quantity must be 0 or more.');
      return;
    }
    setSaving(true);
    try {
      const submitData = {
        ...formData,
        length,
        stockQuantity,
        lowStockThreshold: formData.lowStockThreshold ? parseInt(formData.lowStockThreshold) : null,
        trackInventory: formData.trackInventory
      };

      if (editingBar) {
        await api.put(`/admin/stretcher-bars/${editingBar.id}`, submitData);
        success('Bar updated.');
      } else {
        await api.post('/admin/stretcher-bars', submitData);
        success('Bar created.');
      }
      
      fetchBars();
      resetForm();
    } catch (error: any) {
      console.error('Failed to save bar:', error);
      showError('Failed to save bar: ' + (error.response?.data?.message || error.message));
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (bar: StretcherBar): void => {
    setEditingBar(bar);
    setFormData({
      length: bar.length.toString(),
      stockQuantity: bar.stockQuantity.toString(),
      lowStockThreshold: bar.lowStockThreshold?.toString() || '',
      trackInventory: bar.trackInventory
    });
    setShowForm(true);
  };

  const handleDelete = async (): Promise<void> => {
    if (!confirmDeleteId) return;
    setDeleting(true);
    try {
      await api.delete(`/admin/stretcher-bars/${confirmDeleteId}`);
      fetchBars();
      success('Bar deleted.');
    } catch (error: any) {
      console.error('Failed to delete bar:', error);
      showError('Failed to delete bar: ' + (error.response?.data?.message || error.message));
    } finally {
      setDeleting(false);
      setConfirmDeleteId(null);
    }
  };

  const adjustStock = async (id: number, quantity: number): Promise<void> => {
    setAdjusting(true);
    try {
      await api.post(`/admin/stretcher-bars/${id}/adjust-stock`, { quantity });
      fetchBars();
      success('Stock adjusted.');
    } catch (error: any) {
      console.error('Failed to adjust stock:', error);
      showError('Failed to adjust stock: ' + (error.response?.data?.message || error.message));
    } finally {
      setAdjusting(false);
    }
  };

  const resetForm = (): void => {
    setFormData({
      length: '',
      stockQuantity: '',
      lowStockThreshold: '',
      trackInventory: true
    });
    setEditingBar(null);
    setShowForm(false);
  };

  const fetchLowStockBars = async (): Promise<void> => {
    try {
      const response = await api.get<StretcherBar[]>('/admin/stretcher-bars/low-stock');
      setBars(response.data);
    } catch (error) {
      console.error('Failed to fetch low stock bars:', error);
    }
  };

  if (loading) return <div className="admin-loading">Loading bars...</div>;

  return (
    <div className="admin-stretcher-bars">
      <div className="admin-header">
        <h1>Stretcher Bar Inventory</h1>
        <div className="header-actions">
          <button className="btn btn-secondary" onClick={fetchLowStockBars}>
            Show Low Stock
          </button>
          <button className="btn btn-secondary" onClick={fetchBars}>
            Show All
          </button>
          <button className="btn btn-primary" onClick={() => setShowForm(!showForm)}>
            {showForm ? 'Cancel' : 'Add New Bar'}
          </button>
        </div>
      </div>

      {showForm && (
        <div className="admin-form">
          <h2>{editingBar ? 'Edit Bar' : 'Add New Bar'}</h2>
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label>Length (inches) *</label>
              <input
                type="number"
                step="0.5"
                value={formData.length}
                onChange={(e) => setFormData({ ...formData, length: e.target.value })}
                required
                placeholder="8"
              />
            </div>

            <div className="form-group">
              <label>Stock Quantity *</label>
              <input
                type="number"
                value={formData.stockQuantity}
                onChange={(e) => setFormData({ ...formData, stockQuantity: e.target.value })}
                required
                placeholder="100"
              />
            </div>

            <div className="form-group">
              <label>Low Stock Threshold</label>
              <input
                type="number"
                value={formData.lowStockThreshold}
                onChange={(e) => setFormData({ ...formData, lowStockThreshold: e.target.value })}
                placeholder="20"
              />
              <small>Alert when stock falls below this number</small>
            </div>

            <div className="form-group">
              <label>
                <input
                  type="checkbox"
                  checked={formData.trackInventory}
                  onChange={(e) => setFormData({ ...formData, trackInventory: e.target.checked })}
                />
                Track Inventory
              </label>
            </div>

            <div className="form-actions">
              <button type="submit" className="btn btn-primary" disabled={saving}>
                {saving && <span className="btn-spinner" aria-hidden="true"></span>}
                {editingBar ? 'Update' : 'Create'} Bar
              </button>
              <button type="button" className="btn btn-secondary" onClick={resetForm} disabled={saving}>
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="admin-table">
        <table>
          <thead>
            <tr>
              <th>Length (inches)</th>
              <th>Stock Quantity</th>
              <th>Low Stock Threshold</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {bars.length === 0 ? (
              <tr>
                <td colSpan={5}>No bars found</td>
              </tr>
            ) : (
              bars.map((bar) => (
                <tr key={bar.id} className={bar.isLowStock ? 'low-stock' : ''}>
                  <td><strong>{bar.length}"</strong></td>
                  <td>{bar.stockQuantity}</td>
                  <td>{bar.lowStockThreshold || 'N/A'}</td>
                  <td>
                    <span className={`badge ${bar.isInStock ? 'badge-success' : 'badge-danger'}`}>
                      {bar.isInStock ? 'In Stock' : 'Out of Stock'}
                    </span>
                    {bar.isLowStock && (
                      <span className="badge badge-warning" style={{ marginLeft: '5px' }}>
                        Low Stock
                      </span>
                    )}
                  </td>
                  <td>
                    <div className="action-buttons">
                      <button
                        className="btn btn-sm btn-primary"
                        onClick={() => handleEdit(bar)}
                      >
                        Edit
                      </button>
                      <button
                        className="btn btn-sm btn-success"
                        onClick={() => {
                          setAdjustTargetId(bar.id);
                          setAdjustMode('add');
                        }}
                      >
                        Add Stock
                      </button>
                      <button
                        className="btn btn-sm btn-warning"
                        onClick={() => {
                          setAdjustTargetId(bar.id);
                          setAdjustMode('remove');
                        }}
                      >
                        Remove Stock
                      </button>
                      <button
                        className="btn btn-sm btn-danger"
                        onClick={() => setConfirmDeleteId(bar.id)}
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      <DeleteConfirmModal
        isOpen={confirmDeleteId !== null}
        onClose={() => setConfirmDeleteId(null)}
        onConfirm={handleDelete}
        loading={deleting}
        itemName={confirmDeleteId ? `${bars.find(b => b.id === confirmDeleteId)?.length}" bar` : undefined}
      />
      <SimpleFormModal
        isOpen={adjustMode !== null && adjustTargetId !== null}
        title={adjustMode === 'add' ? 'Add Stock' : 'Remove Stock'}
        fields={[
          { name: 'quantity', label: 'Quantity', type: 'number', required: true, min: '1' }
        ]}
        submitLabel={adjustMode === 'add' ? 'Add Stock' : 'Remove Stock'}
        loading={adjusting}
        onSubmit={(values) => {
          const qty = parseInt(values.quantity || '');
          if (Number.isNaN(qty) || qty <= 0) {
            warning('Quantity must be greater than 0.');
            return;
          }
          if (adjustTargetId && adjustMode) {
            const amount = adjustMode === 'add' ? qty : -qty;
            adjustStock(adjustTargetId, amount);
            setAdjustMode(null);
            setAdjustTargetId(null);
          }
        }}
        onClose={() => {
          setAdjustMode(null);
          setAdjustTargetId(null);
        }}
      />
    </div>
  );
};

export default AdminStretcherBars;
